import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/**
 * 采购链接管理 — lists the 30-day links generated per 采购员 for a quote, with
 * copy + revoke. Extracted from pages/Quotes.jsx (2026-06 refactor).
 */
export default function ProcurementLinksDialog({ open, onOpenChange, links, onRevoke }) {
  const { t } = useLanguage();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="procurement-links-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base">{t("quote.links.title")}</DialogTitle>
          <DialogDescription className="text-xs">{t("quote.links.hint")}</DialogDescription>
        </DialogHeader>
        {links.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8" data-testid="procurement-links-empty">
            {t("quote.links.empty")}
          </p>
        ) : (
          <div className="divide-y divide-border/60 border border-border rounded-md">
            {links.map((l) => (
              <div key={l.token} className="p-3 space-y-1.5" data-testid={`procurement-link-row-${l.buyer}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold">{l.buyer}</span>
                  <span className="text-[11px] px-1.5 py-0.5 rounded border border-border bg-secondary/40 font-mono">
                    {t("quote.links.items").replace("{n}", String(l.item_count))}
                  </span>
                  <span className={`text-[11px] px-1.5 py-0.5 rounded font-mono ${l.submission_count > 0 ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" : "bg-secondary/40 text-muted-foreground"}`}
                    data-testid={`procurement-link-submissions-${l.buyer}`}>
                    {t("quote.links.submissions").replace("{n}", String(l.submission_count))}
                  </span>
                  {(l.revoked || l.expired) && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-destructive/10 text-destructive font-medium">
                      {l.revoked ? t("quote.links.revokedTag") : t("quote.links.expiredTag")}
                    </span>
                  )}
                  <span className="text-[11px] text-muted-foreground ml-auto">
                    {t("quote.links.validUntil").replace("{date}", (l.expires_at || "").slice(0, 10))}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Input readOnly value={l.url} className="text-xs font-mono h-8"
                    onFocus={(e) => e.target.select()} data-testid={`procurement-link-url-${l.buyer}`} />
                  <Button type="button" variant="outline" size="sm" className="h-8 shrink-0"
                    onClick={() => { navigator.clipboard?.writeText(l.url); toast.success(t("quote.editor.linkCopied")); }}
                    data-testid={`procurement-link-copy-${l.buyer}`}>
                    {t("quote.editor.linkCopy")}
                  </Button>
                  {!l.revoked && (
                    <Button type="button" variant="outline" size="sm"
                      className="h-8 shrink-0 text-destructive hover:bg-destructive/10"
                      onClick={() => onRevoke(l.token)}
                      data-testid={`procurement-link-revoke-${l.buyer}`}>
                      {t("quote.links.revoke")}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
